import { Fetcher, HttpMethod } from "../Fetcher";
import { InternationalisationService } from "@/services/InternationalisationService";
import { ApplicationConfigFile } from "@/types/front/ApplicationConfigFile";
import { ApplicationLightResult, ApplicationResult } from "@/types/generated/UsedGeneratedTypes";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Options = { [key: string]: any }; // Options générales sous forme de dictionnaire

// ─── Interfaces de dépendances injectables ─────────────────────────────────────

export interface ApplicationServiceRouter {
  push: (path: string) => void;
}

export interface ApplicationServiceAlert {
  toastWarn: (message: string, error?: unknown, time?: number) => void;
  toastError: (message: string, error?: unknown, state?: unknown, time?: number) => void;
}

export interface ApplicationServiceI18n {
  t: (key: string) => string;
}

export interface ApplicationServiceDependencies {
  router?: ApplicationServiceRouter | null;
  alertService?: ApplicationServiceAlert | null;
  i18n?: ApplicationServiceI18n | null;
}

export class ApplicationService extends Fetcher {
  static INSTANCE: ApplicationService = new ApplicationService();
  static #dependencies: ApplicationServiceDependencies = {};

  private constructor() {
    super();
  }

  static configureService(deps: ApplicationServiceDependencies): void {
    ApplicationService.#dependencies = { ...ApplicationService.#dependencies, ...deps };
  }

  static resetDependencies(): void {
    ApplicationService.#dependencies = {};
  }

  protected get _router(): ApplicationServiceRouter | null {
    return ApplicationService.#dependencies.router ?? null;
  }

  protected get _alertService(): ApplicationServiceAlert | null {
    return ApplicationService.#dependencies.alertService ?? null;
  }

  protected get _i18n(): ApplicationServiceI18n | null {
    return ApplicationService.#dependencies.i18n ?? null;
  }

  async validateConfiguration(
    method: HttpMethod,
    applicationConfigFile: ApplicationConfigFile,
    options: Options = {},
  ): Promise<void> {
    return this.postChunck(
      method,
      "validate-configuration",
      { file: applicationConfigFile.file },
      true,
      options,
    );
  }

  async createApplication(
    method: HttpMethod,
    applicationConfigFile: ApplicationConfigFile,
    comment: string,
    options: Options = {},
  ): Promise<void> {
    return this.postChunck(
      method,
      `applications/${applicationConfigFile.name}`, {
        file: applicationConfigFile.file,
        comment: comment,
      },
      true, options);
  }

  async getApplications(
    method: HttpMethod | ((message: string) => void),
    filter: string[],
  ): Promise<void> {
    this.getChunck(method, "applications", { filter });
  }

  // TODO Type : Should throw and let the caller do the catch with redirection
  async getApplication(name: string, filter: string[]): Promise<LocalApplicationResult | undefined> {
    try {
      const application: ApplicationResult = await this.get(`applications/${name}`, { filter });
      return InternationalisationService.INSTANCE.mergeInternationalization(application);
    }
    catch (e) {
      console.error("getApplication", e);
      this._router?.push("/applications");
    }
  }

  async changeConfiguration(
    method: HttpMethod,
    applicationConfigFile: ApplicationConfigFile,
    comment: string,
    options: Options = {},
  ): Promise<void> {
    return this.postChunck(method, `applications/${applicationConfigFile.name}/configuration`, {
      file: applicationConfigFile.file,
      comment: comment,
    }, true, options);
  }

  async downloadApplicationUploadBundle(applicationName: string, withData: boolean): Promise<boolean> {
    let ok: boolean | Response = false;
    try {
      const response = await this.send(
        `applications/${applicationName}/upload-bundle?withData=${withData}&locale=${this.getUserPrefLocale()}`,
      );
      if (response instanceof Response) {
        ok = response.ok;
      }
      else {
        // Normalise en booléen si un objet { ok: boolean } est renvoyé par un mock
        // ou si une valeur truthy/falsy est renvoyée directement
        ok = typeof response === "object" && response != null && "ok" in response
          // @ts-expect-error: propriété potentielle ok
          ? !!response.ok
          : !!response;
      }
    }
    catch (e) {
      console.error("error", e);
    }
    this.displayMessage(ok);
    return ok;
  }

  async uploadApplicationUploadBundle(
    method: (chunk: unknown) => unknown,
    applicationName: string,
    file: File,
    options: Options = {},
  ): Promise<unknown> {
    return this.postChunck(
      method as (message: string) => void,
      `applications/${applicationName}/download-bundle`,
      { file },
      true,
      options,
    );
  }

  displayMessage(ok: boolean): void {
    const t = (key: string): string => this._i18n?.t(key) ?? key;
    if (ok) {
      this._alertService?.toastWarn(
        t("message.async-service-started"),
        undefined,
        8000,
      );
    }
    else {
      this._alertService?.toastError(
        t("message.async-service-failed"),
        undefined,
        undefined,
        8000,
      );
    }
  }

  isApplicationUser(application: ApplicationLightResult): boolean {
    const roles = application.currentApplicationUserRolesResult?.applicationRoles !== null
      ? application.currentApplicationUserRolesResult?.applicationRoles
      : [];
    return (
      roles.includes("applicationManager")
      || roles.includes("userManager")
      || application.hasSignedCharte
    );
  }
}
